import { MenuItemNames, useNavStore } from "@shared/stores/navigation";
import { subscriptionsService } from "@shared/services/subscriptions";
import { entitlementsService } from "@shared/services/entitlements";
import { permissions } from "@shared/services/permissions";
import { BomType } from "@shared/types/common";
import { EntitlementName } from "@shared/types/localTypes/entitlements.ts";
import { useAuthStore } from "@shared/stores/auth.ts";
import isImpactCenterAllowed from "@shared/services/impact-center/isImpactCenterAllowed.ts";
import { useAuthenticatedRelease } from "@shared/use/useAuthenticatedRelease.ts";

export const navigationService = {
  manageMenuItemsOnSubscriptionChanged: (subscriptionNumber: string) => {
    Object.values(MenuItemNames).forEach((menuItemName) => {
      useNavStore().setMenuItemVisibility(
        menuItemName,
        menuItemsCalculator[menuItemName].isVisible(subscriptionNumber),
      );
      useNavStore().setMenuItemDisabled(
        menuItemName,
        !menuItemsCalculator[menuItemName].isEnabled(subscriptionNumber),
      );
      useNavStore().setMenuItemNew(
        menuItemName,
        menuItemsCalculator[menuItemName].isNew(subscriptionNumber),
      );
    });
  },
};

type MenuItemsCalculator = {
  [key in MenuItemNames]: {
    isVisible: (subscriptionNumber: string) => boolean;
    isEnabled: (subscriptionNumber: string) => boolean;
    notificationCount: (subscriptionNumber: string) => number;
    isNew: (subscriptionNumber: string) => boolean;
  };
};

export const menuItemsCalculator: MenuItemsCalculator = {
  [MenuItemNames.Dashboard]: {
    isVisible: (subscriptionNumber) =>
      !!subscriptionsService.findCachedSubscription(subscriptionNumber),
    isEnabled: (subscriptionNumber) => {
      const subscriptionToCheck =
        subscriptionsService.findCachedSubscription(subscriptionNumber);
      if (!subscriptionToCheck) {
        return false;
      }
      const meta = subscriptionToCheck.meta;
      return meta.isSaas ? !!meta.hasServers : true;
    },
    notificationCount: () => 0,
    isNew: () => false,
  },
  [MenuItemNames.ImpactCenter]: {
    isVisible: (subscriptionNumber) => {
      const subscriptionToCheck =
        subscriptionsService.findCachedSubscription(subscriptionNumber);
      return (
        !!subscriptionToCheck &&
        !!subscriptionToCheck.meta.isSaas &&
        !!subscriptionToCheck.meta.hasXray &&
        !subscriptionToCheck.meta.isEnterprisePlusHybrid &&
        isImpactCenterAllowed()
      );
    },
    isEnabled: (subscriptionNumber) => {
      const subscriptionToCheck =
        subscriptionsService.findCachedSubscription(subscriptionNumber);
      if (!subscriptionToCheck) {
        return false;
      }
      const meta = subscriptionToCheck.meta;
      return meta.isSaas ? !!meta.hasServers : true;
    },
    notificationCount: () => 0,
    isNew: () => false,
  },
  [MenuItemNames.UsageExplorer]: {
    isVisible: (subscriptionNumber) => {
      return !!subscriptionsService.findCachedSubscription(subscriptionNumber);
    },
    isEnabled: (subscriptionNumber) => {
      const subscriptionToCheck =
        subscriptionsService.findCachedSubscription(subscriptionNumber);
      if (!subscriptionToCheck) {
        return false;
      }

      const meta = subscriptionToCheck.meta;
      if (meta.isSaas && !meta.hasServers) {
        return false;
      }
      return (
        !!meta.isSaas || !!meta.hasJas || !!meta.hasCuration || !!meta.hasML
      );
    },
    notificationCount: () => 0,
    isNew: () => false,
  },
  [MenuItemNames.Billing]: {
    isVisible: (subscriptionNumber) => {
      const subscriptionToCheck =
        subscriptionsService.findCachedSubscription(subscriptionNumber);
      return (
        !!subscriptionToCheck &&
        !!subscriptionToCheck.meta.isSaas &&
        permissions.has("billing:view", "subscription", subscriptionNumber)
      );
    },
    isEnabled: (subscriptionNumber) => {
      const subscriptionToCheck =
        subscriptionsService.findCachedSubscription(subscriptionNumber);
      if (!subscriptionToCheck) {
        return false;
      }

      const meta = subscriptionToCheck.meta;
      return meta.isSaas ? !!meta.hasServers : true;
    },
    notificationCount: () => 0,
    isNew: () => false,
  },
  [MenuItemNames.Topology]: {
    isVisible: (subscriptionNumber) => {
      const subscriptionToCheck =
        subscriptionsService.findCachedSubscription(subscriptionNumber);
      return (
        !!subscriptionToCheck &&
        !!subscriptionToCheck.meta.isSaas &&
        !!subscriptionToCheck.meta.isMultiJpdsSupportedForSubscription &&
        permissions.has("topology:view", "subscription", subscriptionNumber)
      );
    },
    isEnabled: () => true,
    notificationCount: (subscriptionNumber) => {
      const subscriptionToCheck =
        subscriptionsService.findCachedSubscription(subscriptionNumber);
      if (!subscriptionToCheck || subscriptionToCheck.state === "ONHOLD") {
        return 0;
      }
      return (
        subscriptionToCheck.nbInstanceConfigurable +
        subscriptionToCheck.nbEdgesConfigurable +
        subscriptionToCheck.nbColdsConfigurable
      );
    },
    isNew: () => false,
  },
  [MenuItemNames.Security]: {
    isVisible: (subscriptionNumber) => {
      const subscriptionToCheck =
        subscriptionsService.findCachedSubscription(subscriptionNumber);
      return (
        !!subscriptionToCheck &&
        !!subscriptionToCheck.meta.isSaas &&
        permissions.has("security:view", "subscription", subscriptionNumber)
      );
    },
    isEnabled: (subscriptionNumber) => {
      const subscriptionToCheck =
        subscriptionsService.findCachedSubscription(subscriptionNumber);
      if (!subscriptionToCheck) {
        return false;
      }

      const meta = subscriptionToCheck.meta;
      return meta.isSaas ? !!meta.hasServers : true;
    },
    notificationCount: () => 0,
    isNew: () => false,
  },
  [MenuItemNames.SupportTickets]: {
    isVisible: (subscriptionNumber) => {
      const subscriptionToCheck =
        subscriptionsService.findCachedSubscription(subscriptionNumber);
      return (
        !!subscriptionToCheck &&
        !!subscriptionToCheck.meta.isSaas &&
        (!!subscriptionToCheck.meta.isEnterprisePlus ||
          !!subscriptionToCheck.meta.isEnterpriseX ||
          !!subscriptionToCheck.meta.isEnterpriseTeam) &&
        !subscriptionToCheck.meta.isTrial &&
        permissions.has(
          "supportTickets:read",
          "subscription",
          subscriptionNumber,
        )
      );
    },
    isEnabled: (subscriptionNumber) => {
      const subscriptionToCheck =
        subscriptionsService.findCachedSubscription(subscriptionNumber);
      if (!subscriptionToCheck) {
        return false;
      }

      const meta = subscriptionToCheck.meta;
      return meta.isSaas ? !!meta.hasServers : true;
    },
    notificationCount: () => 0,
    isNew: () => false,
  },
  [MenuItemNames.Users]: {
    isVisible: (subscriptionNumber) => {
      const subscriptionToCheck =
        subscriptionsService.findCachedSubscription(subscriptionNumber);
      return (
        !!subscriptionToCheck &&
        permissions.has("manageUsers:view", "subscription", subscriptionNumber)
      );
    },
    isEnabled: (subscriptionNumber) => {
      const subscriptionToCheck =
        subscriptionsService.findCachedSubscription(subscriptionNumber);
      if (!subscriptionToCheck) {
        return false;
      }

      const meta = subscriptionToCheck.meta;
      return meta.isSaas ? !!meta.hasServers : true;
    },
    notificationCount: () => 0,
    isNew: () => false,
  },
  [MenuItemNames.NotificationCenter]: {
    isVisible: (subscriptionNumber) => {
      const subscriptionToCheck =
        subscriptionsService.findCachedSubscription(subscriptionNumber);
      return (
        !!subscriptionToCheck &&
        !!subscriptionToCheck.meta.isSaas &&
        permissions.has(
          "notifications:read",
          "subscription",
          subscriptionNumber,
        )
      );
    },
    isEnabled: (subscriptionNumber) => {
      const subscriptionToCheck =
        subscriptionsService.findCachedSubscription(subscriptionNumber);
      if (!subscriptionToCheck) {
        return false;
      }

      const meta = subscriptionToCheck.meta;
      return meta.isSaas ? !!meta.hasServers : true;
    },
    notificationCount: () => 0,
    isNew: () => false,
  },
  [MenuItemNames.ActivityLog]: {
    isVisible: (subscriptionNumber) => {
      const subscriptionToCheck =
        subscriptionsService.findCachedSubscription(subscriptionNumber);
      return (
        !!subscriptionToCheck &&
        permissions.has("activityLog:read", "subscription", subscriptionNumber)
      );
    },
    isEnabled: (subscriptionNumber) => {
      const subscriptionToCheck =
        subscriptionsService.findCachedSubscription(subscriptionNumber);
      if (!subscriptionToCheck) {
        return false;
      }

      const meta = subscriptionToCheck.meta;
      return meta.isSaas ? !!meta.hasServers : true;
    },
    notificationCount: () => 0,
    isNew: () => false,
  },
  [MenuItemNames.SupportContacts]: {
    isVisible: (subscriptionNumber) => {
      const subscriptionToCheck =
        subscriptionsService.findCachedSubscription(subscriptionNumber);
      const isSubscriptionPro =
        subscriptionToCheck?.meta.isCloudPro ||
        subscriptionToCheck?.meta.isCloudProX ||
        subscriptionToCheck?.meta.isProTeam ||
        subscriptionToCheck?.bom.type === BomType.PRO;

      const isProNotMPSubscription =
        isSubscriptionPro && !subscriptionToCheck?.meta.isMP;
      return (
        !!subscriptionToCheck &&
        !subscriptionToCheck.meta.isTrial &&
        !subscriptionToCheck.meta.isFree &&
        !subscriptionToCheck.meta.isEnterprisePlusHybrid &&
        !isProNotMPSubscription &&
        permissions.has(
          "supportContacts:view",
          "subscription",
          subscriptionNumber,
        )
      );
    },
    isEnabled: (subscriptionNumber) => {
      const subscriptionToCheck =
        subscriptionsService.findCachedSubscription(subscriptionNumber);
      if (!subscriptionToCheck) {
        return false;
      }

      const meta = subscriptionToCheck.meta;
      return meta.isSaas ? !!meta.hasServers : true;
    },
    notificationCount: () => 0,
    isNew: () => false,
  },
  [MenuItemNames.Settings]: {
    isVisible: (subscriptionNumber) => {
      const subscriptionToCheck =
        subscriptionsService.findCachedSubscription(subscriptionNumber);
      return (
        !!subscriptionToCheck &&
        permissions.has("settings:view", "subscription", subscriptionNumber)
      );
    },
    isEnabled: (subscriptionNumber) => {
      const subscriptionToCheck =
        subscriptionsService.findCachedSubscription(subscriptionNumber);
      if (!subscriptionToCheck) {
        return false;
      }

      const meta = subscriptionToCheck.meta;
      return meta.isSaas ? !!meta.hasServers : true;
    },
    notificationCount: () => 0,
    isNew: () => false,
  },
  [MenuItemNames.JPD]: {
    isVisible: (subscriptionNumber) => {
      const subscriptionToCheck =
        subscriptionsService.findCachedSubscription(subscriptionNumber);
      return (
        !!subscriptionToCheck &&
        !!subscriptionToCheck.meta.isSelfHosted &&
        permissions.has("jpd:view", "subscription", subscriptionNumber)
      );
    },
    isEnabled: () => true,
    notificationCount: () => 0,
    isNew: () => false,
  },
  // Self-hosted only: the patch repositories this mints against exist for private installations of
  // Artifactory and Distribution. Hidden too once the subscription is two weeks past expiry.
  // api-proxy enforces the same rules, so hiding the item here is convenience, not the security
  // boundary.
  [MenuItemNames.AuthenticatedRelease]: {
    isVisible: (subscriptionNumber) => {
      const subscriptionToCheck =
        subscriptionsService.findCachedSubscription(subscriptionNumber);
      return (
        !!subscriptionToCheck &&
        !!subscriptionToCheck.meta.isSelfHosted &&
        !subscriptionToCheck.meta.isTrial &&
        useAuthenticatedRelease().isWithinGracePeriod(subscriptionToCheck)
      );
    },
    isEnabled: () => true,
    notificationCount: () => 0,
    isNew: () => false,
  },
  [MenuItemNames.Licenses]: {
    isVisible: (subscriptionNumber) => {
      const subscriptionToCheck =
        subscriptionsService.findCachedSubscription(subscriptionNumber);
      return (
        !!subscriptionToCheck &&
        !!subscriptionToCheck.meta.isSelfHosted &&
        permissions.has("licenses:view", "subscription", subscriptionNumber)
      );
    },
    isEnabled: () => true,
    notificationCount: () => 0,
    isNew: () => false,
  },
  [MenuItemNames.ThreatIntelligence]: {
    isVisible: (subscriptionNumber) => {
      const subscriptionToCheck =
        subscriptionsService.findCachedSubscription(subscriptionNumber);
      const authStore = useAuthStore();
      return !!(
        authStore?.user?.mfaActivated &&
        !!subscriptionToCheck &&
        !!entitlementsService.getEntitlement(
          subscriptionToCheck,
          EntitlementName.THREAT_INTELLIGENCE,
        ) &&
        permissions.has(
          "threatIntelligence:read",
          "subscription",
          subscriptionNumber,
        )
      );
    },
    isEnabled: () => true,
    notificationCount: () => 0,
    isNew: () => false,
  },
  [MenuItemNames.Sandbox]: {
    isVisible: (subscriptionNumber) =>
      !!subscriptionNumber && import.meta.env.DEV,
    isEnabled: () => true,
    notificationCount: () => 0,
    isNew: () => false,
  },
};
